import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertCircle, ExternalLink, Loader2, Store } from "lucide-react";
import { getIcone } from "@/lib/icones";

import { POLOS, getPoloLabel } from "@/constants/polos";
import { FORNECEDOR_ORIGEM, FORNECEDOR_STATUS } from "@/constants/enums";

const ESTADOS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];


const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;

const schema = z.object({
  nome: z.string().min(1, "Nome é obrigatório"),
  razao_social: z.string().nullable().optional(),
  cnpj: z.string().nullable().optional().refine(val => !val || cnpjRegex.test(val), "CNPJ inválido"),
  segmento_id: z.string().min(1, "Segmento é obrigatório"),
  telefone: z.string().min(1, "WhatsApp/Telefone é obrigatório"),
  email: z.string().email("E-mail inválido").nullable().optional().or(z.literal("")),
  site: z.string().url("URL inválida").nullable().optional().or(z.literal("")),
  instagram: z.string().nullable().optional(),
  uf: z.string().min(1, "UF é obrigatória"),
  cidade: z.string().min(1, "Cidade é obrigatória"),
  polo: z.string().nullable().optional(),
  pedido_minimo: z.string().nullable().optional(),
  atende_varejo: z.boolean(),
  descricao: z.string().max(400, "Máximo 400 caracteres").nullable().optional(),
  logo_url: z.string().nullable().optional(),
  status: z.enum([FORNECEDOR_STATUS.PENDENTE, FORNECEDOR_STATUS.APROVADO, FORNECEDOR_STATUS.REJEITADO]),
  verificado: z.boolean(),
  destaque: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface FornecedorFormProps {
  initialData?: any;
  onSuccess: (keepOpen?: boolean) => void;
  onCancel: () => void;
}

export function FornecedorForm({ initialData, onSuccess, onCancel }: FornecedorFormProps) {
  const [isCheckingCnpj, setIsCheckingCnpj] = useState(false);
  const [cnpjError, setCnpjError] = useState<{ id: string; nome: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const isEditing = !!initialData;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialData ? {
      ...initialData,
      status: initialData.status || FORNECEDOR_STATUS.APROVADO,
      verificado: !!initialData.verificado,
      destaque: !!initialData.destaque,
      atende_varejo: !!initialData.atende_varejo,
      razao_social: initialData.razao_social || "",
      email: initialData.email || "",
      site: initialData.site || "",
      instagram: initialData.instagram || "",
      polo: initialData.polo || "",
      pedido_minimo: initialData.pedido_minimo || "",
      descricao: initialData.descricao || "",
      logo_url: initialData.logo_url || null,
    } : {
      status: FORNECEDOR_STATUS.APROVADO,
      verificado: false,
      destaque: false,
      atende_varejo: false,
      nome: "",
      cnpj: "",
      telefone: "",
      uf: "",
      cidade: "",
      segmento_id: "",
      razao_social: "",
      email: "",
      site: "",
      instagram: "",
      polo: "",
      pedido_minimo: "",
      descricao: "",
      logo_url: null,
    },
  });

  const { data: segmentos } = useQuery({
    queryKey: ["segmentos"],
    queryFn: async () => {
      const { data } = await supabase.from("segmentos").select("*").order("nome");
      return data || [];
    },
  });

  const validateCnpjDigits = (cnpj: string) => {
    const numbers = cnpj.replace(/\D/g, "");
    if (numbers.length !== 14) return false;
    if (/^(\d)\1{13}$/.test(numbers)) return false;

    let size = numbers.length - 2;
    let digits = numbers.substring(0, size);
    const pos = numbers.substring(size);
    let sum = 0;
    let p = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += parseInt(digits.charAt(size - i)) * p--;
      if (p < 2) p = 9;
    }
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(pos.charAt(0))) return false;

    size = size + 1;
    digits = numbers.substring(0, size);
    sum = 0;
    p = size - 7;
    for (let i = size; i >= 1; i--) {
      sum += parseInt(digits.charAt(size - i)) * p--;
      if (p < 2) p = 9;
    }
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(pos.charAt(1))) return false;

    return true;
  };

  const handleCnpjChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 14) value = value.substring(0, 14);
    
    if (value.length > 12) value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, "$1.$2.$3/$4-$5");
    else if (value.length > 8) value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{1,4})/, "$1.$2.$3/$4");
    else if (value.length > 5) value = value.replace(/^(\d{2})(\d{3})(\d{1,3})/, "$1.$2.$3");
    else if (value.length > 2) value = value.replace(/^(\d{2})(\d{1,3})/, "$1.$2");
    
    form.setValue("cnpj", value);

    if (value.length === 18 && !isEditing) {
      if (!validateCnpjDigits(value)) {
        form.setError("cnpj", { message: "Dígito verificador inválido" });
        return;
      }
      
      setIsCheckingCnpj(true);
      const { data } = await supabase
        .from("fornecedores")
        .select("id, nome")
        .eq("cnpj", value)
        .maybeSingle();
      
      setIsCheckingCnpj(false);
      if (data) {
        setCnpjError({ id: data.id, nome: data.nome });
        form.setError("cnpj", { message: "Este CNPJ já está na lista" });
      } else {
        setCnpjError(null);
        form.clearErrors("cnpj");
      }
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 11) value = value.substring(0, 11);
    
    if (value.length > 10) value = value.replace(/^(\d{2})(\d{5})(\d{4}).*/, "($1) $2-$3");
    else if (value.length > 6) value = value.replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
    else if (value.length > 2) value = value.replace(/^(\d{2})(\d{0,4})/, "($1) $2");
    else if (value.length > 0) value = value.replace(/^(\d*)/, "($1");
    
    form.setValue("telefone", value);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("O arquivo deve ter no máximo 2MB");
      return;
    }

    try {
      setUploading(true);
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Sessão expirada. Entre novamente.");
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("logos-fornecedores")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("logos-fornecedores")
        .getPublicUrl(filePath);

      form.setValue("logo_url", publicUrl);
      toast.success("Logo enviada!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar logo");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (values: FormValues, keepOpen = false) => {
    if (cnpjError && !isEditing) return;

    try {
      const normalizedInstagram = values.instagram ? values.instagram.replace("@", "").trim() : null;

      const payload: any = {
        ...values,
        cnpj: values.cnpj || null,
        instagram: normalizedInstagram,
        origem: isEditing ? initialData.origem : FORNECEDOR_ORIGEM.ADMIN,
        owner_id: isEditing ? initialData.owner_id : null,
      };

      if (payload.verificado && !payload.cnpj) {
        toast.error("Não é possível verificar um fornecedor sem CNPJ");
        return;
      }

      if (isEditing) {
        const { error } = await supabase
          .from("fornecedores")
          .update(payload)
          .eq("id", initialData.id);
        if (error) throw error;
        toast.success("Fornecedor atualizado!");
      } else {
        const { error } = await supabase
          .from("fornecedores")
          .insert(payload);
        if (error) throw error;
        toast.success("Fornecedor cadastrado!");
      }

      if (keepOpen) {
        const { segmento_id, uf, cidade, polo } = values;
        form.reset({
          segmento_id,
          uf,
          cidade,
          polo,
          status: FORNECEDOR_STATUS.APROVADO,
          verificado: false,
          destaque: false,
          atende_varejo: false,
          nome: "",
          cnpj: "",
          telefone: "",
          email: "",
          site: "",
          instagram: "",
          razao_social: "",
          descricao: "",
          pedido_minimo: "",
          logo_url: null
        });
      } else {
        onSuccess();
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao salvar fornecedor");
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((v) => onSubmit(v))} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider border-b pb-2">Identificação</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="cnpj"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CNPJ</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input 
                        {...field} 
                        value={field.value || ""}
                        onChange={handleCnpjChange}
                        disabled={isEditing}
                        placeholder="00.000.000/0000-00" 
                      />
                      {isCheckingCnpj && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                      )}
                    </div>
                  </FormControl>
                  {cnpjError && !isEditing && (
                    <div className="flex items-start gap-2 mt-1 text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <div>
                        <span>CNPJ já vinculado a: <strong>{cnpjError.nome}</strong></span>
                        <Link 
                          to={`/fornecedores/${cnpjError.id}` as any}
                          className="flex items-center gap-1 text-amber-700 underline font-medium ml-1"
                        >
                          Abrir ficha <ExternalLink className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome da empresa* (Fantasia)</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="razao_social"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Razão Social</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="segmento_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Segmento*</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um segmento" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {segmentos?.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <div className="flex items-center gap-2">
                            {(() => {
                              const Icon = getIcone(s.icone);
                              return <Icon className="w-3.5 h-3.5 opacity-60" />;
                            })()}
                            {s.nome}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider border-b pb-2">Contato</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="telefone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>WhatsApp/Telefone*</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} onChange={handlePhoneChange} placeholder="(00) 00000-0000" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail comercial</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="site"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Site</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} placeholder="https://..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="instagram"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Instagram</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} placeholder="@usuario" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider border-b pb-2">Localização e Detalhes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="uf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>UF*</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="UF" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ESTADOS.map((uf) => (
                        <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cidade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cidade*</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="polo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Polo atacadista</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um polo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {POLOS.map((p) => (
                        <SelectItem key={p.nome} value={p.nome}>
                          {getPoloLabel(p)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />

                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pedido_minimo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pedido mínimo</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} placeholder="Ex: R$ 300 ou 12 peças" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <FormField
              control={form.control}
              name="atende_varejo"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel>Atende varejo?</FormLabel>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">Logo</label>
              <div className="flex items-center gap-4">
                {form.watch("logo_url") && (
                  <div className="w-12 h-12 rounded-lg border overflow-hidden shrink-0">
                    <img src={form.watch("logo_url")!} alt="Logo" className="w-full h-full object-contain" />
                  </div>
                )}
                <div className="relative grow">
                  <Input 
                    type="file" 
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                    className="cursor-pointer"
                  />
                  {uploading && (
                    <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500">Máx 2MB (logos-fornecedores)</p>
            </div>
          </div>

          <FormField
            control={form.control}
            name="descricao"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Textarea 
                      {...field} 
                      value={field.value || ""}
                      placeholder="Fale um pouco sobre o fornecedor..."
                      className="resize-none h-24"
                    />
                    <span className="absolute bottom-2 right-2 text-xs text-gray-400">
                      {field.value?.length || 0}/400
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="p-4 bg-gray-50 rounded-lg space-y-4 border">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider border-b pb-2">Controles de Admin</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="aprovado">Aprovado</SelectItem>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="rejeitado">Rejeitado</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="verificado"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <FormLabel>Verificado</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </div>
                  <FormDescription className="text-xs">
                    Ative apenas se a empresa for cliente Gopage
                  </FormDescription>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="destaque"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>Destaque</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-end gap-3 pt-6 border-t mt-8">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          {!isEditing && (
            <Button 
              type="button" 
              variant="outline" 
              className="border-[#C6F764] text-[#0C1A12] hover:bg-[#C6F764]/10"
              onClick={form.handleSubmit((v) => onSubmit(v, true))}
            >
              Salvar e cadastrar outro
            </Button>
          )}
          <Button 
            type="submit" 
            className="bg-[#C6F764] text-[#0C1A12] hover:bg-[#b5e653]"
          >
            {isEditing ? "Salvar Alterações" : "Salvar Fornecedor"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
